import { useEffect, useRef, useState, type HTMLAttributes } from 'react';
import type { Editor } from '@tiptap/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronRight, GripVertical } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  formatCurrency,
  formatProductPrice,
  isCurrencyAmount,
  parseCurrencyAmount,
} from '@/lib/format';
import {
  findInvestmentPhaseRow,
  investmentPhaseRows,
  moveInvestmentPhaseRow,
  selectWorkspacePosition,
  type InvestmentDestination,
} from '@/editor/quoteWorkspace';
import {
  setInvestmentRowCell,
  setInvestmentRowRate,
  setInvestmentRowShowHours,
  setInvestmentRowProductVersion,
} from '@/editor/investmentRowSettings';
import { calculateInvestmentProduct, updateInvestmentProduct } from '@/editor/investmentProduct';
import { useInvestmentInspectorStore } from '@/store/investmentInspectorStore';
import { outdatedRowVersion, useProductVersionStore } from '@/store/productVersionStore';
import InvestmentRateSelect from './InvestmentRateSelect';
import ProductRowVersionFlag from './ProductRowVersionFlag';
import { GroupVersionUpdate, ProductVersionUpdate } from './InvestmentInspector';

const ROW_DISPLAY_TRIGGER = 'h-[30px] w-auto max-w-full gap-2 bg-card text-[11px] text-foreground';

function RowInput({
  label,
  value,
  onChange,
  numeric = false,
  autoFocus = false,
  formatValue,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  numeric?: boolean;
  autoFocus?: boolean;
  formatValue?: (value: string) => string;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <label className="workspace-row-field">
      <span>{label}</span>
      <input
        className="workspace-input"
        value={draft}
        autoFocus={autoFocus}
        type={numeric ? 'number' : 'text'}
        min={numeric ? '0' : undefined}
        step={numeric ? 'any' : undefined}
        placeholder={numeric ? '0' : 'Omschrijving'}
        onChange={(event) => {
          const next = event.target.value;
          setDraft(next);
          if (
            !numeric ||
            next === '' ||
            (Number.isFinite(event.target.valueAsNumber) && event.target.valueAsNumber >= 0)
          )
            onChange(next);
        }}
        onBlur={() => {
          if (numeric && draft && (!Number.isFinite(Number(draft)) || Number(draft) < 0))
            setDraft(value);
          else if (formatValue) {
            const formatted = formatValue(draft);
            if (formatted !== draft) onChange(formatted);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
      />
    </label>
  );
}

/** A row in the list; its grip sorts the row in the table, like the grip in the document. */
function SortablePhaseRow({
  id,
  label,
  disabled,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & { id: string; label: string; disabled: boolean }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });
  return (
    <div
      {...props}
      ref={setNodeRef}
      data-dragging={isDragging || undefined}
      style={{ transform: CSS.Transform.toString(transform && { ...transform, x: 0 }), transition }}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        className="workspace-icon-button workspace-row-drag"
        disabled={disabled}
        {...attributes}
        {...listeners}
        aria-label={`${label} verslepen om te sorteren`}
        title="Versleep om te sorteren"
      >
        <GripVertical size={14} aria-hidden="true" />
      </button>
      {children}
    </div>
  );
}

/** Standalone rows are edited in the list; products alone open a calculation layer. */
export default function InvestmentPhaseRowsEditor({
  editor,
  destination,
}: {
  editor: Editor;
  destination: InvestmentDestination;
}) {
  const phase = useInvestmentInspectorStore((state) => state.phase);
  const versions = useProductVersionStore();
  const list = useRef<HTMLDivElement>(null);
  const rows = investmentPhaseRows(editor.state.doc, destination);
  const header = editor.state.doc.nodeAt(destination.headerPos);
  const showHourColumns = header?.attrs.showHourColumns !== false;
  const activeIndex = phase?.tableId === destination.tableId ? phase.row?.index : undefined;
  const selectedRowIsEmpty = rows.some(
    (row) => row.index === activeIndex && !row.product && !row.cells[0]
  );
  const money = (amount: number | null) =>
    amount === null ? 'Op aanvraag' : formatCurrency(amount / 100, destination.currency);

  useEffect(() => {
    if (activeIndex === undefined) return;
    const selected = list.current?.querySelector(`[data-row-index="${activeIndex}"]`);
    if (!selected?.contains(editor.view.dom.ownerDocument.activeElement))
      selected?.scrollIntoView({ block: 'nearest' });
    if (selectedRowIsEmpty && !editor.view.hasFocus())
      selected
        ?.querySelector<HTMLInputElement>('.workspace-row-name input')
        ?.focus({ preventScroll: true });
  }, [activeIndex, selectedRowIsEmpty, editor]);

  const setCell = (index: number, cellIndex: number, value: string) => {
    const target = findInvestmentPhaseRow(editor.state.doc, destination.tableId, index);
    if (target) setInvestmentRowCell(editor, target.row, target.pos, cellIndex, value);
  };

  const [sorting, setSorting] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const sortId = (row: (typeof rows)[number]) => row.product?.id ?? `row-${row.index}`;
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setSorting(false);
    if (!over || active.id === over.id) return;
    const from = rows.find((row) => sortId(row) === active.id);
    const to = rows.find((row) => sortId(row) === over.id);
    if (from && to) moveInvestmentPhaseRow(editor, destination.tableId, from.index, to.index);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={() => setSorting(true)}
      onDragCancel={() => setSorting(false)}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={rows.map(sortId)} strategy={verticalListSortingStrategy}>
        <div
          ref={list}
          className="workspace-phase-rows"
          data-investment-phase-rows={destination.tableId}
          data-overview-sorting={sorting || undefined}
          role="list"
          aria-label={`${destination.title} regels`}
          onKeyDown={(event) => {
            if (
              event.defaultPrevented ||
              !(event.metaKey || event.ctrlKey) ||
              event.key.toLowerCase() !== 'z'
            )
              return;
            event.preventDefault();
            event.stopPropagation();
            if (event.shiftKey) editor.commands.redo();
            else editor.commands.undo();
          }}
        >
          {rows.map((row) => {
            const product = row.product;
            const selected = activeIndex === row.index;
            const selectRow = () => {
              const target = findInvestmentPhaseRow(
                editor.state.doc,
                destination.tableId,
                row.index
              );
              if (target) selectWorkspacePosition(editor, target.pos + 3);
            };
            const computed = product ? calculateInvestmentProduct(product) : null;
            const rowVersion = outdatedRowVersion(versions, row.productId, row.productVersionId);
            return (
              <SortablePhaseRow
                key={sortId(row)}
                id={sortId(row)}
                label={row.title}
                disabled={rows.length < 2}
                role="listitem"
                data-row-index={row.index}
                data-row-kind={product ? 'product' : 'standalone'}
                className={`workspace-editable-row${selected ? ' is-selected' : ''}`}
                data-pricing-type={destination.pricingType}
                onFocusCapture={() => {
                  // Keep the page layout in place until the first click completes.
                  if (phase?.tableId === destination.tableId && !product && !selected) selectRow();
                }}
              >
                <div className="workspace-row-name">
                  {product ? (
                    <button type="button" className="workspace-row-product" onClick={selectRow}>
                      <strong>{row.title}</strong>
                      <span>
                        {computed?.lines.length
                          ? `${computed.lines.filter((line) => !line.excluded).length} van ${computed.lines.length} regels inbegrepen`
                          : 'Vaste prijs'}{' '}
                        · Prijsopbouw bewerken
                      </span>
                    </button>
                  ) : (
                    <RowInput
                      label={`Omschrijving regel ${row.index}`}
                      value={row.cells[0] ?? ''}
                      autoFocus={selected && !row.cells[0] && !editor.view.hasFocus()}
                      onChange={(value) => setCell(row.index, 0, value)}
                    />
                  )}
                  <ProductRowVersionFlag
                    productId={row.productId}
                    productVersionId={row.productVersionId}
                    onSelect={selectRow}
                  >
                    {product ? (
                      <GroupVersionUpdate
                        product={product}
                        onChange={(updated) => updateInvestmentProduct(editor, updated, true)}
                      />
                    ) : rowVersion ? (
                      <ProductVersionUpdate
                        row={{
                          ...row,
                          count: rows.length,
                          productId: row.productId ?? null,
                          productVersionId: row.productVersionId ?? null,
                        }}
                        pricingType={destination.pricingType}
                        currency={destination.currency}
                        version={rowVersion}
                        onChange={(index, change) => {
                          const target = findInvestmentPhaseRow(
                            editor.state.doc,
                            destination.tableId,
                            index
                          );
                          if (target)
                            setInvestmentRowProductVersion(editor, target.row, target.pos, change);
                        }}
                      />
                    ) : undefined}
                  </ProductRowVersionFlag>
                </div>
                {!product && destination.pricingType === 'hourly' && (
                  <>
                    <div className="workspace-row-rate">
                      <InvestmentRateSelect
                        rateKey={row.rateKey}
                        amount={row.cells[1] ?? ''}
                        currency={destination.currency}
                        onSelect={(key, cents) => {
                          const target = findInvestmentPhaseRow(
                            editor.state.doc,
                            destination.tableId,
                            row.index
                          );
                          if (target)
                            setInvestmentRowRate(
                              editor,
                              target.row,
                              target.pos,
                              key,
                              cents,
                              destination.currency
                            );
                        }}
                        onCustomChange={(value) => setCell(row.index, 1, value)}
                      />
                    </div>
                    <RowInput
                      label="Uren"
                      numeric
                      value={row.cells[2] ?? ''}
                      onChange={(value) => setCell(row.index, 2, value)}
                    />
                  </>
                )}
                {!product && destination.pricingType === 'recurring' && (
                  <RowInput
                    label="Bedrag"
                    value={row.cells[1] ?? ''}
                    onChange={(value) => setCell(row.index, 1, value)}
                    formatValue={(value) =>
                      value.trim() && isCurrencyAmount(value)
                        ? formatCurrency(parseCurrencyAmount(value), destination.currency)
                        : value
                    }
                  />
                )}
                <div className="workspace-row-amount">
                  <span>
                    {destination.pricingType === 'fixed' && !product
                      ? 'Prijs in fasetotaal'
                      : 'Regeltotaal'}
                  </span>
                  {product ? (
                    <button
                      type="button"
                      onClick={selectRow}
                      aria-label={`${row.title} prijsopbouw bewerken`}
                    >
                      <strong>
                        {computed
                          ? formatProductPrice(computed, destination.currency)
                          : money(row.amount)}
                      </strong>
                      <ChevronRight size={16} />
                    </button>
                  ) : destination.pricingType !== 'fixed' ? (
                    <strong>
                      {row.cells[destination.pricingType === 'hourly' ? 3 : 1] || '—'}
                    </strong>
                  ) : null}
                </div>
                {product ? (
                  <label className="workspace-row-display">
                    Op de offerte
                    <Select
                      value={product.display}
                      onValueChange={(value) =>
                        updateInvestmentProduct(
                          editor,
                          { ...product, display: value === 'expanded' ? 'expanded' : 'collapsed' },
                          true
                        )
                      }
                    >
                      <SelectTrigger
                        aria-label={`${product.productName} klantweergave`}
                        className={ROW_DISPLAY_TRIGGER}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent data-investment-inspector>
                        <SelectItem value="expanded">Alle regels</SelectItem>
                        <SelectItem value="collapsed">Eén regel</SelectItem>
                      </SelectContent>
                    </Select>
                  </label>
                ) : destination.pricingType === 'hourly' ? (
                  <label className="workspace-row-display">
                    Op de offerte
                    <Select
                      value={(row.showHours ?? showHourColumns) ? 'hours' : 'fixed'}
                      onValueChange={(value) => {
                        const target = findInvestmentPhaseRow(
                          editor.state.doc,
                          destination.tableId,
                          row.index
                        );
                        if (target)
                          setInvestmentRowShowHours(
                            editor,
                            target.row,
                            target.pos,
                            value === 'hours'
                          );
                      }}
                    >
                      <SelectTrigger
                        aria-label={`Prijsweergave regel ${row.index}`}
                        className={ROW_DISPLAY_TRIGGER}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent data-investment-inspector>
                        <SelectItem value="hours">Uren en uurtarief</SelectItem>
                        <SelectItem value="fixed">Alleen vaste prijs</SelectItem>
                      </SelectContent>
                    </Select>
                  </label>
                ) : null}
              </SortablePhaseRow>
            );
          })}
          {!rows.length && (
            <p className="workspace-overview-hint">Deze fase heeft nog geen regels.</p>
          )}
        </div>
      </SortableContext>
    </DndContext>
  );
}
